import {
    QuickStartCIAppPlatformData,
    QuickStartCILocalData,
    QuickStartCIScanDetailsConfirmationData,
    QuickStartCIScanDetailsData,
    QuickStartCIUploadSigningSecretsData,
    QuickStartCIUploadVCSIntegrationData,
    QuickStartCIWhatIsQuickstartCIData,
} from './../actions/ui';
import { QuickStartCISetupStepType, SetupStepType } from './../typescriptHelpers';
// Local storage keys

import { AutomationType, IntegrationType, ReleaseNav, ReleaseStepType } from '../types';
import { OrgSummaryPreferences } from '../types/summaryView';
import { StringKeyedDictionary } from '../typescriptHelpers';

export const REMEMBER_ME_STORAGE_KEY = 'user-email';
export const REMEMBER_ME_SSO_STORAGE_KEY = 'sso-user-email';
export const DEFAULT_APP_IN_ORG = 'default-app-in-org';
export const DEFAULT_APP_IN_APP_GROUP = 'default-app-in-app-group';
export const LAST_VIEWED_RELEASE_STEP_FOR_RELEASE = 'last-viewed-release-step-for-release';
export const USER_HAS_VIEWED_ONBOARDING = 'user-has-viewed-onboarding';
export const LAST_LOGIN = 'last-login';
export const SETUP_STEPS_DATA = 'setup-steps-data';
export const QUICK_START_SETUP_STEPS_DATA = 'quick-start-setup-steps-data';
export const SEEN_NEW_AUTOMATIONS = 'seen-new-automations';
export const GET_NOTIFIED_COMING_SOON_INTEGRATIONS = 'get-notified-coming-soon-integrations';
export const QUALITATIVE_ONBOARDING_DISMISSED = 'qualitative-onboarding-dismissed';
export const QUICK_START_CI_DISMISSED = 'quick-start-ci-dismissed';
export const REGISTER_DEVICE_DISMISSED = 'register-device-dismissed';
export const RELEASE_FEEDBACK = 'release-feedback';
export const FR_ITEMS_FILTERS = 'fr-items-filters';
export const FR_ITEMS_SORT = 'fr-items-sort';
export const FT_ITEMS_ONLY_FIXES = 'ft-items-only-fixes';
export const FT_ITEMS_GROUP_BY_TICKET = 'ft-items-group-by-ticket';
export const AUTH_REDIRECT_ROUTE = 'auth-redirect-route';
export const APP_SUMMARY_VIEW_PREFERENCES = 'app-summary-view-preferences';
export const ORG_SUMMARY_VIEW_PREFERENCES = 'org-summary-view-preferences';
export const SELECTED_DESTINATION_IDS = 'selected-destination-ids';
/* return string-keyed dictionary of userId: orgId for default org */
export const DEFAULT_ORG_FOR_USER = 'default-org-for-user';

/* Getters and settings for local storage */

export const setItem: (key: string, value: unknown, storage?: Storage) => void = (
    key,
    value,
    storage = window.localStorage,
) => {
    storage?.setItem(key, JSON.stringify(value));
};

export const deleteItem: (key: string, storage?: Storage) => void = (key, storage = window.localStorage) => {
    storage?.removeItem(key);
};

export const getItem: <T>(key: string, storage?: Storage) => T = (key, storage = window.localStorage) => {
    const value = storage?.getItem(key);

    if (value) {
        return JSON.parse(value);
    }

    return null;
};

/* helpers */

export const getDefaultOrgForUser: (userId: string) => number = (userId) => {
    const data: StringKeyedDictionary<number> = getItem(DEFAULT_ORG_FOR_USER);

    return data ? data[userId] : null;
};

export const getLastSelectedReleaseStepForRelease: (releaseId: string) => ReleaseStepType | ReleaseNav = (
    releaseId,
) => {
    const lastSelectedReleaseStepForRelease: StringKeyedDictionary<string> = getItem(
        LAST_VIEWED_RELEASE_STEP_FOR_RELEASE,
    );

    const releaseStep = lastSelectedReleaseStepForRelease ? lastSelectedReleaseStepForRelease[releaseId] : null;

    return releaseStep as ReleaseStepType | ReleaseNav;
};

export const clearLastSelectedReleaseStepForRelease: (releaseId: string) => void = (releaseId) => {
    const lastSelectedReleaseStepForRelease: StringKeyedDictionary<string> = getItem(
        LAST_VIEWED_RELEASE_STEP_FOR_RELEASE,
    );

    lastSelectedReleaseStepForRelease[releaseId] = null;

    setItem(LAST_VIEWED_RELEASE_STEP_FOR_RELEASE, lastSelectedReleaseStepForRelease);
};

export const getDefaultAppIdInGroupId: (groupId: string) => string = (groupId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_APP_GROUP);

    return data ? data[groupId] : null;
};

export const setDefaultAppIdParamInGroupId: (groupId: string, appIdParam: string) => void = (groupId, appIdParam) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_APP_GROUP);

    const updatedData = {
        ...data,
        [groupId]: appIdParam,
    };

    setItem(DEFAULT_APP_IN_APP_GROUP, updatedData);
};

export const getDefaultAppIdParamInOrg: (orgId: string) => string = (orgId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_APP_IN_ORG);

    return data ? data[orgId] : null;
};

export const setDefaultAppIdParamInOrg: (orgId: string, appIdParam: string) => void = (orgId, appIdParam) => {
    const data = getItem(DEFAULT_APP_IN_ORG);

    const dataType = typeof data;
    let updatedData = {};

    if (dataType === 'string') {
        updatedData[orgId] = appIdParam;
    } else {
        const objectData = data as StringKeyedDictionary<string>;

        updatedData = {
            ...objectData,
            [orgId]: appIdParam,
        };
    }

    setItem(DEFAULT_APP_IN_ORG, updatedData);
};

// Default orgId for userId

export const getDefaultOrgIdForUserId: (userId: string) => string = (userId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_ORG_FOR_USER);

    return data ? data[userId] : null;
};

export const setDefaultOrgIdForUserId: (userId: string, orgId: string) => void = (userId, orgId) => {
    const data: StringKeyedDictionary<string> = getItem(DEFAULT_ORG_FOR_USER);

    const updatedData = {
        ...data,
        [userId]: orgId,
    };

    setItem(DEFAULT_ORG_FOR_USER, updatedData);
};

//  Setup steps data

interface SetupStepsLocalData {
    hasBeenVisited: boolean;
}

export const getAllSetupStepsLocalData: () => Record<SetupStepType, SetupStepsLocalData> = () => {
    return getItem(SETUP_STEPS_DATA) ?? ({} as Record<SetupStepType, SetupStepsLocalData>);
};

export const getSetupStepLocalData: (appId: string, stepType: SetupStepType) => SetupStepsLocalData = (
    appId,
    stepType,
) => {
    return getAllSetupStepsLocalData()[`${appId}#${stepType}`];
};

export const setSetupStepLocalData: (stepType: SetupStepType, appId: string, data: SetupStepsLocalData) => void = (
    stepType,
    appId,
    data,
) => {
    const previousData = getAllSetupStepsLocalData();
    setItem(SETUP_STEPS_DATA, {
        ...previousData,
        [`${appId}#${stepType}`]: data,
    });
};

// quick start ci local data

export type QuickStartCISetupStepData<T> =
    T extends QuickStartCISetupStepType.PlatformConfirmation ? QuickStartCIAppPlatformData
    : T extends QuickStartCISetupStepType.ScanDetails ? QuickStartCIScanDetailsData
    : T extends QuickStartCISetupStepType.ScanDetailsConfirmation ? QuickStartCIScanDetailsConfirmationData
    : T extends QuickStartCISetupStepType.UploadSecrets ? QuickStartCIUploadSigningSecretsData
    : T extends QuickStartCISetupStepType.VCSIntegration ? QuickStartCIUploadVCSIntegrationData
    : T extends QuickStartCISetupStepType.WhatIsQuickStartCI ? QuickStartCIWhatIsQuickstartCIData
    : object;

export const getAllQuickStartCILocalData = () => {
    return (getItem(QUICK_START_SETUP_STEPS_DATA) ?? {}) as Record<
        `${string}#${QuickStartCISetupStepType}`,
        QuickStartCILocalData
    >;
};

export const setQuickStartCILocalData: <T extends QuickStartCISetupStepType>(
    stepType: T,
    appId: string,
    data: QuickStartCISetupStepData<T>,
) => void = (stepType, appId, data) => {
    const previousData = getAllQuickStartCILocalData();

    const key = `${appId}#${stepType}`;
    setItem(QUICK_START_SETUP_STEPS_DATA, {
        ...(previousData as object),
        [key]: {
            ...previousData[key],
            ...data,
        },
    });
};

//  New automations seen
export const getSeenNewAutomations: () => Partial<Record<AutomationType, true>> = () => {
    return getItem(SEEN_NEW_AUTOMATIONS);
};

export const setSeenNewAutomations: (data: Partial<Record<AutomationType, true>>) => void = (data) => {
    setItem(SEEN_NEW_AUTOMATIONS, data);
};

// Get notified for new integrations - saves an array of IntegrationType's to be notified about, keyed off orgId

export const getPreferencesForNotifyingOfComingSoonIntegrations: (orgId: string) => IntegrationType[] = (orgId) => {
    const preferences = getItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS) ?? {};

    return preferences[orgId] ?? [];
};

export const setPreferencesForNotifyingOfComingSoonIntegrations: (orgId: string, data: IntegrationType[]) => void = (
    orgId,
    data,
) => {
    const currentPrefs: StringKeyedDictionary<IntegrationType[]> = getItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS) ?? {};

    const updatedPrefs: StringKeyedDictionary<IntegrationType[]> = {
        ...currentPrefs,
        [orgId]: data,
    };

    setItem(GET_NOTIFIED_COMING_SOON_INTEGRATIONS, updatedPrefs);
};

// Dismiss qualitative onboarding

export const getOnboardingDismissedForAppId: (appId: string) => boolean = (appId) => {
    const preferences = getItem(QUALITATIVE_ONBOARDING_DISMISSED) ?? {};

    return preferences[appId] ?? false;
};

export const setOnboardingDismissedForAppId: (appId: string, isDismissed: boolean) => void = (appId, isDismissed) => {
    const currentPrefs: Record<string, boolean> = getItem(QUALITATIVE_ONBOARDING_DISMISSED) ?? {};

    const updatedPrefs: Record<string, boolean> = {
        ...currentPrefs,
        [appId]: isDismissed,
    };

    setItem(QUALITATIVE_ONBOARDING_DISMISSED, updatedPrefs);
};

// Summary preferences

type AppSummaryPreferencesMap = Record<string, Partial<OrgSummaryPreferences>>;

const getAppPreferencesMap = (): AppSummaryPreferencesMap => {
    return getItem<AppSummaryPreferencesMap>(APP_SUMMARY_VIEW_PREFERENCES) ?? {};
};

export const getAppSummaryPreferences = (appId: string): Partial<OrgSummaryPreferences> => {
    const appPrefs = getAppPreferencesMap();
    return appPrefs[appId] ?? {};
};

export const setAppSummaryPreferences = (
    appId: string,
    preferences: Partial<OrgSummaryPreferences>,
): Partial<OrgSummaryPreferences> => {
    const appPrefs = getAppPreferencesMap();
    const currentPrefs = appPrefs[appId] ?? {};
    const updatedPrefs = {
        ...currentPrefs,
        ...preferences,
    };

    setItem(APP_SUMMARY_VIEW_PREFERENCES, {
        ...appPrefs,
        [appId]: updatedPrefs,
    });

    return updatedPrefs;
};

export const getOrgSummaryPreferences = (): Partial<OrgSummaryPreferences> => {
    return getItem<Partial<OrgSummaryPreferences>>(ORG_SUMMARY_VIEW_PREFERENCES) ?? {};
};

export const setOrgSummaryPreferences = (
    preferences: Partial<OrgSummaryPreferences>,
): Partial<OrgSummaryPreferences> => {
    const currentPrefs = getOrgSummaryPreferences();
    const updatedPrefs = {
        ...currentPrefs,
        ...preferences,
    };
    setItem(ORG_SUMMARY_VIEW_PREFERENCES, updatedPrefs);
    return updatedPrefs;
};

export const getSelectedDestinationIds: () => string[] | null = () => {
    const preferences = getOrgSummaryPreferences();
    return preferences.selectedDestinationIds ?? null;
};

export const setSelectedDestinationIds: (ids: string[]) => void = (ids) => {
    setOrgSummaryPreferences({ selectedDestinationIds: ids });
};

// Dismiss quick start ci

export const getQuickStartCIDismissedForAppId: (appId: string) => boolean = (appId) => {
    const preferences = getItem(QUICK_START_CI_DISMISSED) ?? {};

    return preferences[appId] ?? false;
};

export const setQuickStartCIDismissedForAppId: (appId: string, isDismissed: boolean) => void = (appId, isDismissed) => {
    const currentPrefs: Record<string, boolean> = getItem(QUICK_START_CI_DISMISSED) ?? {};

    const updatedPrefs: Record<string, boolean> = {
        ...currentPrefs,
        [appId]: isDismissed,
    };

    setItem(QUICK_START_CI_DISMISSED, updatedPrefs);
};

// Dismiss register device

export const getRegisterDeviceDismissedForAppId: (appId: string) => boolean = (appId) => {
    const preferences = getItem(REGISTER_DEVICE_DISMISSED) ?? {};

    return preferences[appId] ?? false;
};

export const setRegisterDeviceDismissedForAppId: (appId: string, isDismissed: boolean) => void = (
    appId,
    isDismissed,
) => {
    const currentPrefs: Record<string, boolean> = getItem(REGISTER_DEVICE_DISMISSED) ?? {};

    const updatedPrefs: Record<string, boolean> = {
        ...currentPrefs,
        [appId]: isDismissed,
    };

    setItem(REGISTER_DEVICE_DISMISSED, updatedPrefs);
};

// View / dismiss release feedback survey

export const getUserViewedReleaseSurveyForRelease: (releaseId: string) => boolean = (releaseId) => {
    const releasePilotViewedSurveyForReleaseMap = getItem(RELEASE_FEEDBACK) ?? {};

    return releasePilotViewedSurveyForReleaseMap[releaseId] ?? false;
};

export const setUserViewedReleaseSurveyForRelease: (releaseId: string, didView: boolean) => void = (
    releaseId,
    didView,
) => {
    const releasePilotViewedSurveyForReleaseMap: Record<string, boolean> = getItem(RELEASE_FEEDBACK) ?? {};

    const updatedReleasePilotViewedSurveyForReleaseMap: Record<string, boolean> = {
        ...releasePilotViewedSurveyForReleaseMap,
        [releaseId]: didView,
    };

    setItem(RELEASE_FEEDBACK, updatedReleasePilotViewedSurveyForReleaseMap);
};

// Feature readiness items filters

export const getFeatureItemsFiltersStoredParams: () => string = () => {
    return getItem(FR_ITEMS_FILTERS) ?? '';
};

export const setFeatureItemsFiltersStoredParams: (params: string) => void = (params) => {
    setItem(FR_ITEMS_FILTERS, params);
};

export const clearFeatureItemsFiltersStoredParams: () => void = () => {
    deleteItem(FR_ITEMS_FILTERS);
};

// Feature readiness items sort

export const getFeatureItemsSortStoredParams: () => string = () => {
    return getItem(FR_ITEMS_SORT) ?? '';
};

export const setFeatureItemsSortStoredParams: (params: string) => void = (params) => {
    setItem(FR_ITEMS_SORT, params);
};

export const clearFeatureItemsSortStoredParams: () => void = () => {
    deleteItem(FR_ITEMS_SORT);
};

// Feature items only fixes

export const getFeatureItemsOnlyFixesStoredParams: () => string = () => {
    return getItem(FT_ITEMS_ONLY_FIXES) ?? '';
};

export const setFeatureItemsOnlyFixesStoredParams: (params: string) => void = (params) => {
    setItem(FT_ITEMS_ONLY_FIXES, params);
};

export const clearFeatureItemsOnlyFixesStoredParams: () => void = () => {
    deleteItem(FT_ITEMS_ONLY_FIXES);
};

// Feature items group by ticket

export const getFeatureItemsGroupByTicketStoredParams: () => string = () => {
    return getItem(FT_ITEMS_GROUP_BY_TICKET) ?? '';
};

export const setFeatureItemsGroupByTicketStoredParams: (params: string) => void = (params) => {
    setItem(FT_ITEMS_GROUP_BY_TICKET, params);
};

export const clearFeatureItemsGroupByTicketStoredParams: () => void = () => {
    deleteItem(FT_ITEMS_GROUP_BY_TICKET);
};

// Selected destination IDs (per-app keyed storage)

export const getSelectedDestinationIdsForApp = (appId: string): string[] => {
    const data = getItem<Record<string, string[]>>(SELECTED_DESTINATION_IDS);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
    return data[appId] ?? [];
};

export const setSelectedDestinationIdsForApp = (appId: string, destinationIds: string[]): void => {
    const existing = getItem<Record<string, string[]>>(SELECTED_DESTINATION_IDS);
    const data: Record<string, string[]> =
        existing && typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
    data[appId] = destinationIds;
    setItem(SELECTED_DESTINATION_IDS, data);
};

// Auth redirect route

const AUTH_REDIRECT_ROUTE_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface AuthRedirectRouteData {
    route: string;
    timestamp: number;
}

export const getAuthRedirectRoute: () => string = () => {
    const data: AuthRedirectRouteData = getItem(AUTH_REDIRECT_ROUTE);
    console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', data);

    if (!data) {
        return '';
    }

    // Check if the route has expired
    const now = Date.now();
    if (now - data.timestamp > AUTH_REDIRECT_ROUTE_TTL_MS) {
        console.debug('[REDIRECT DEBUG]', 'getAuthRedirectRoute', 'route has expired');
        // Route has expired, clear it
        clearAuthRedirectRoute();
        return '';
    }

    return data.route;
};

export const setAuthRedirectRoute: (route: string) => void = (route) => {
    console.debug('[REDIRECT DEBUG]', 'setAuthRedirectRoute', route);
    const data: AuthRedirectRouteData = {
        route,
        timestamp: Date.now(),
    };
    setItem(AUTH_REDIRECT_ROUTE, data);
};

export const clearAuthRedirectRoute: () => void = () => {
    deleteItem(AUTH_REDIRECT_ROUTE);
};

// Helper function to check and redirect to saved route if it exists
export const checkAndRedirectToSavedRoute: () => void = () => {
    console.debug('[REDIRECT DEBUG]', 'checkAndRedirectToSavedRoute');
    const savedRoute = getAuthRedirectRoute();
    if (savedRoute) {
        console.debug('[REDIRECT DEBUG]', 'checkAndRedirectToSavedRoute', 'redirecting to', savedRoute);
        clearAuthRedirectRoute();
        window.location.replace(savedRoute);
    }
};
